import { type FleetReportDto, type MeDto, REPORT_CATEGORIES, REPORT_OPEN, REPORT_STATUSES, type ReportStatus } from '@bqcrm/shared';
import { Anchor, Button, Card, Drawer, Group, Image, Select, SimpleGrid, Stack, Table, Text, Textarea, Timeline } from '@mantine/core';
import { IconAlertTriangle, IconPlus } from '@tabler/icons-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { api } from '../../api';
import { can } from '../../auth';
import { BlocksUseBadge, FleetReportStatusBadge } from '../../components/Badges';
import { PageHeader } from '../../components/PageHeader';
import { QueryState } from '../../components/QueryState';
import { EmptyState, SectionCard } from '../../components/ui';
import { formatDateTime } from '../../format';
import { notifyError, notifyOk } from '../../notify';
import { FleetShell, kmPl, ReportFormModal, useFleetInvalidate, useFleetVehicles, vehicleLabel } from './common';

function ReportDrawer({ id, manage, onClose }: { id: string | null; manage: boolean; onClose: () => void }) {
  const q = useQuery({ queryKey: ['fleet', 'report', id], queryFn: () => api<FleetReportDto>(`/fleet/reports/${id}`), enabled: !!id });
  const invalidate = useFleetInvalidate();
  const r = q.data;
  const [status, setStatus] = useState<ReportStatus | null>(null);
  const [note, setNote] = useState('');
  useEffect(() => {
    setStatus(r?.status ?? null);
    setNote('');
  }, [r?.id, r?.status]);
  const save = useMutation({
    mutationFn: () => api<FleetReportDto>(`/fleet/reports/${id}/status`, { method: 'POST', body: { status, note: note.trim() } }),
    onSuccess: () => {
      notifyOk('Zmieniono status zgłoszenia');
      invalidate();
    },
    onError: notifyError,
  });
  const needsNote = status === 'odrzucone' && note.trim().length < 3;
  return (
    <Drawer opened={!!id} onClose={onClose} position="right" size="lg" title={r ? `Zgłoszenie ${r.number}` : 'Zgłoszenie'}>
      <QueryState isLoading={q.isLoading} error={q.error}>
        {r && (
          <Stack gap="md">
            <Group gap="xs">
              <FleetReportStatusBadge status={r.status} />
              {r.blocksUse && <BlocksUseBadge />}
            </Group>
            <SimpleGrid cols={2} spacing="sm">
              <div>
                <Text size="xs" c="dimmed">
                  Pojazd
                </Text>
                <Anchor component={Link} to={`/flota/pojazdy/${r.vehicleId}?zakladka=zgloszenia`} size="sm" fw={600}>
                  {r.plate}
                </Anchor>
              </div>
              <div>
                <Text size="xs" c="dimmed">
                  Kategoria
                </Text>
                <Text size="sm">{REPORT_CATEGORIES[r.category]}</Text>
              </div>
              <div>
                <Text size="xs" c="dimmed">
                  Zgłaszający
                </Text>
                <Text size="sm">{r.reporter.name}</Text>
              </div>
              <div>
                <Text size="xs" c="dimmed">
                  Zgłoszono
                </Text>
                <Text size="sm">{formatDateTime(r.createdAt)}</Text>
              </div>
              {r.km !== null && (
                <div>
                  <Text size="xs" c="dimmed">
                    Przebieg
                  </Text>
                  <Text size="sm">{kmPl(r.km)}</Text>
                </div>
              )}
            </SimpleGrid>
            <Text size="sm" style={{ whiteSpace: 'pre-line' }}>
              {r.description}
            </Text>
            {r.blocksUse && (
              <Text size="xs" c="dimmed">
                „Nie da się jeździć” to ocena zgłaszającego. Wyłączenie z użytkowania — wpis niedostępności na karcie pojazdu.{' '}
                <Anchor component={Link} to={`/flota/pojazdy/${r.vehicleId}?zakladka=dostepnosc`} size="xs">
                  Dostępność pojazdu
                </Anchor>
              </Text>
            )}
            {r.photos.length > 0 && (
              <SimpleGrid cols={3} spacing="xs">
                {r.photos.map((p) => (
                  <a key={p.id} href={p.url} target="_blank" rel="noreferrer">
                    <Image src={p.url} h={110} radius="sm" fit="cover" alt="Zdjęcie zgłoszenia" />
                  </a>
                ))}
              </SimpleGrid>
            )}
            {manage && (
              <SectionCard title="Obsługa">
                <Stack gap="sm">
                  <Select label="Status" data={Object.entries(REPORT_STATUSES).map(([value, label]) => ({ value, label }))} value={status} onChange={(x) => x && setStatus(x as ReportStatus)} allowDeselect={false} />
                  <Textarea label={status === 'odrzucone' ? 'Uzasadnienie odrzucenia' : 'Notatka'} required={status === 'odrzucone'} autosize minRows={2} value={note} onChange={(e) => setNote(e.currentTarget.value)} />
                  <Text size="xs" c="dimmed">
                    Serwis z kilkoma zgłoszeniami umawia się w zakładce pojazdu „Terminy i serwisy” → „Umów wizytę”.
                  </Text>
                  <Group justify="flex-end">
                    <Button size="xs" disabled={status === r.status || needsNote} loading={save.isPending} onClick={() => save.mutate()}>
                      Zmień status
                    </Button>
                  </Group>
                </Stack>
              </SectionCard>
            )}
            {r.history.length > 0 && (
              <SectionCard title="Historia">
                <Timeline bulletSize={12} lineWidth={2}>
                  {r.history.map((h, i) => (
                    <Timeline.Item key={i} title={<Text size="sm">{REPORT_STATUSES[h.status]}</Text>}>
                      {h.note && <Text size="sm">{h.note}</Text>}
                      <Text size="xs" c="dimmed">
                        {formatDateTime(h.at)}
                        {h.byName ? ` · ${h.byName}` : ''}
                      </Text>
                    </Timeline.Item>
                  ))}
                </Timeline>
              </SectionCard>
            )}
          </Stack>
        )}
      </QueryState>
    </Drawer>
  );
}

/** Zgłoszenia floty (usterki, opony, serwis): pilne na górze, szczegóły w szufladzie (?zgloszenie=id). */
export function FleetReportsPage({ me }: { me: MeDto }) {
  const q = useQuery({ queryKey: ['fleet', 'reports', 'all'], queryFn: () => api<FleetReportDto[]>('/fleet/reports'), refetchInterval: 60_000, refetchOnWindowFocus: true });
  const vehicles = useFleetVehicles();
  const [params, setParams] = useSearchParams();
  const openId = params.get('zgloszenie');
  const setOpen = (id: string | null) => setParams(id ? { zgloszenie: id } : {}, { replace: true });
  const [filter, setFilter] = useState<string>('otwarte');
  const [adding, setAdding] = useState(false);
  const rows = (q.data ?? [])
    .filter((r) => (filter === 'otwarte' ? REPORT_OPEN.includes(r.status) : filter === 'wszystkie' ? true : r.status === filter))
    .sort((a, b) => Number(b.blocksUse && REPORT_OPEN.includes(b.status)) - Number(a.blocksUse && REPORT_OPEN.includes(a.status)) || b.createdAt.localeCompare(a.createdAt));
  return (
    <FleetShell me={me}>
      <PageHeader
        title="Zgłoszenia"
        description="Usterki i potrzeby zgłaszane przez użytkowników aut i zarządzającego. „Nie da się jeździć” — wyróżnione jako pilne."
        actions={
          can(me, 'flota.zarzadzanie') && (
            <Button leftSection={<IconPlus size={16} />} onClick={() => setAdding(true)} disabled={!vehicles.data?.length}>
              Zgłoś problem
            </Button>
          )
        }
      />
      <Group mb="md">
        <Select
          data={[
            { value: 'otwarte', label: 'Otwarte' },
            { value: 'wszystkie', label: 'Wszystkie' },
            ...Object.entries(REPORT_STATUSES).map(([value, label]) => ({ value, label })),
          ]}
          value={filter}
          onChange={(x) => x && setFilter(x)}
          allowDeselect={false}
          w={{ base: '100%', sm: 220 }}
        />
      </Group>
      <Card padding={0}>
        <QueryState isLoading={q.isLoading} error={q.error}>
          {rows.length ? (
            <Table.ScrollContainer minWidth={820}>
              <Table highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Numer</Table.Th>
                    <Table.Th>Pojazd</Table.Th>
                    <Table.Th>Opis</Table.Th>
                    <Table.Th>Zgłaszający</Table.Th>
                    <Table.Th>Status</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {rows.map((r) => {
                    const urgent = r.blocksUse && REPORT_OPEN.includes(r.status);
                    return (
                      <Table.Tr key={r.id} className="row-link" onClick={() => setOpen(r.id)} style={urgent ? { backgroundColor: 'var(--mantine-color-red-light)' } : undefined}>
                        <Table.Td>
                          <Group gap={4} wrap="nowrap">
                            {urgent && <IconAlertTriangle size={16} color="var(--mantine-color-red-6)" />}
                            <Text size="sm" fw={600}>
                              {r.number}
                            </Text>
                          </Group>
                          <Text size="xs" c="dimmed">
                            {REPORT_CATEGORIES[r.category]}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm" fw={500}>
                            {r.plate}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm" lineClamp={2}>
                            {r.description}
                          </Text>
                          {r.photos.length > 0 && (
                            <Text size="xs" c="dimmed">
                              zdjęcia: {r.photos.length}
                            </Text>
                          )}
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">{r.reporter.name}</Text>
                          <Text size="xs" c="dimmed">
                            {formatDateTime(r.createdAt)}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Group gap={4}>
                            {r.blocksUse && <BlocksUseBadge size="xs" />}
                            <FleetReportStatusBadge status={r.status} />
                          </Group>
                        </Table.Td>
                      </Table.Tr>
                    );
                  })}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          ) : (
            <EmptyState title={filter === 'otwarte' ? 'Brak otwartych zgłoszeń' : 'Brak zgłoszeń w wybranym filtrze'} />
          )}
        </QueryState>
      </Card>
      <ReportDrawer id={openId} manage={can(me, 'flota.zarzadzanie')} onClose={() => setOpen(null)} />
      {adding && <ReportFormModal vehicles={(vehicles.data ?? []).filter((v) => v.status === 'aktywny').map((v) => ({ id: v.id, label: vehicleLabel(v) }))} onClose={() => setAdding(false)} onDone={(r) => setOpen(r.id)} />}
    </FleetShell>
  );
}
